import React from "react";
import { Opportunity } from "../../api/types";
import { Badge } from "@/components/Badge/Badge";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import OpportunityEventTimeline from "../OpportunityEventTimeline/OpportunityEventTimeline";

interface OpportunityDetailProps {
  opportunity: Opportunity | null;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function OpportunityDetail({ opportunity }: OpportunityDetailProps) {
  if (opportunity === null) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select an opportunity to view details
        </p>
      </div>
    );
  }

  const isUnlinked = opportunity.opportunityId === null;

  if (isUnlinked) {
    return (
      <div className="overflow-y-auto h-full p-4 space-y-4">
        <h2 className="text-base font-normal text-gray-900 dark:text-white">
          No Opportunity Linked
        </h2>
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Event Timeline
            </p>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <OpportunityEventTimeline events={opportunity.events} />
          </CardContent>
        </Card>
      </div>
    );
  }

  const companyName = opportunity.company || opportunity.name;
  const hasContact =
    opportunity.firstName ||
    opportunity.lastName ||
    opportunity.emailAddress ||
    opportunity.phoneNumber;
  const hasLocation =
    opportunity.cityName || opportunity.stateCode || opportunity.postalCode;
  const hasTrades = opportunity.trades && opportunity.trades.length > 0;
  const keyDates = [
    { label: "Import date", value: opportunity.importDate },
    { label: "Launch date", value: opportunity.launchDate },
    { label: "Invoice date", value: opportunity.invoiceDate },
  ].filter((d) => d.value !== null) as { label: string; value: string }[];

  return (
    <div className="overflow-y-auto h-full p-4 space-y-4">
      {/* Header card */}
      <Card className="rounded-lg">
        <CardHeader className="px-4 py-4">
          <h2 className="text-base font-normal text-gray-900 dark:text-white">
            {companyName}
          </h2>
          {opportunity.stageName && (
            <div className="mt-1">
              <Badge variant="grey">{opportunity.stageName}</Badge>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Contact section */}
      {hasContact && (
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Contact</p>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-1">
            {(opportunity.firstName || opportunity.lastName) && (
              <p className="text-sm text-gray-900 dark:text-white font-normal">
                {[opportunity.firstName, opportunity.lastName]
                  .filter(Boolean)
                  .join(" ")}
              </p>
            )}
            {opportunity.emailAddress && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {opportunity.emailAddress}
              </p>
            )}
            {opportunity.phoneNumber && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {opportunity.phoneNumber}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Location section */}
      {hasLocation && (
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Location</p>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <p className="text-sm text-gray-900 dark:text-white font-normal">
              {[
                opportunity.cityName,
                opportunity.stateCode,
                opportunity.postalCode,
              ]
                .filter(Boolean)
                .join(", ")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Trades section */}
      {hasTrades && (
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">Trades</p>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="flex flex-wrap gap-1.5">
              {opportunity.trades?.map((trade) => (
                <Badge key={trade} variant="grey">
                  {trade}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key dates section */}
      {keyDates.length > 0 && (
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Key Dates
            </p>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-2">
            {keyDates.map((date) => (
              <div
                className="flex justify-between items-center"
                key={date.label}
              >
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {date.label}
                </span>
                <span className="text-sm text-gray-900 dark:text-white font-normal">
                  {formatDate(date.value)}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Agreement section */}
      {opportunity.agreement !== null && (
        <Card className="rounded-lg">
          <CardHeader className="pb-2 pt-4 px-4">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Agreement
            </p>
          </CardHeader>
          <CardContent className="px-4 pb-4 space-y-1">
            <p className="text-sm text-gray-900 dark:text-white font-normal">
              {opportunity.agreement.company}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {opportunity.agreement.salesperson}
            </p>
            {opportunity.agreement.signedDate && (
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Signed {formatDate(opportunity.agreement.signedDate)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Event timeline section */}
      <Card className="rounded-lg">
        <CardHeader className="pb-2 pt-4 px-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Event Timeline
          </p>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <OpportunityEventTimeline events={opportunity.events} />
        </CardContent>
      </Card>
    </div>
  );
}

export { OpportunityDetail };
export default OpportunityDetail;
